import { useState, useRef, useEffect } from 'react'
import type { AppLockInstance } from '@ascend/applock'
import { useAlert } from '@ascend/alert'
import { Lock, Unlock, Trash2, Shield } from 'lucide-react'

interface LockMenuProps {
  lock: AppLockInstance
}

export function LockMenu({ lock }: LockMenuProps) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { alert, confirm, prompt } = useAlert()

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleLock = () => {
    lock.lock('manual')
    setOpen(false)
  }

  const handleChangePassword = async () => {
    setOpen(false)
    const current = await prompt({
      title: 'Change Password',
      label: 'Current password',
      inputType: 'password',
      confirmLabel: 'Continue',
    })
    if (!current) return
    const newPw = await prompt({
      title: 'New Password',
      message: 'Minimum 4 characters.',
      label: 'New password',
      inputType: 'password',
      confirmLabel: 'Change',
      validate: (v) => (v.trim().length < 4 ? 'Password must be at least 4 characters' : null),
    })
    if (!newPw) return
    try {
      await lock.changePassword(current, newPw)
      await alert({ title: 'Password Changed', message: 'Your password was updated successfully.', type: 'success' })
    } catch {
      await alert({
        title: 'Change Failed',
        message: 'Check your current password and try again.',
        type: 'danger',
      })
    }
  }

  const handleRemovePassword = async () => {
    setOpen(false)
    const current = await prompt({
      title: 'Remove App Lock',
      label: 'Current password',
      inputType: 'password',
      confirmLabel: 'Continue',
    })
    if (!current) return
    const confirmed = await confirm({
      title: 'Remove App Lock?',
      message: 'Your data will remain but won\u2019t be password-protected.',
      danger: true,
      confirmLabel: 'Remove',
    })
    if (!confirmed) return
    try {
      await lock.removePassword(current)
      await alert({ title: 'Lock Removed', type: 'success' })
    } catch {
      await alert({
        title: 'Removal Failed',
        message: 'Check your password and try again.',
        type: 'danger',
      })
    }
  }

  return (
    <div className="lock-menu" ref={menuRef}>
      <button
        type="button"
        className="lock-menu__trigger"
        onClick={() => setOpen(!open)}
        aria-label="Lock settings"
        aria-expanded={open}
      >
        <Shield size={18} />
      </button>
      {open && (
        <div className="lock-menu__dropdown" role="menu">
          <button type="button" className="lock-menu__item" onClick={handleLock} role="menuitem">
            <Lock size={16} /> Lock Now
          </button>
          <button type="button" className="lock-menu__item" onClick={handleChangePassword} role="menuitem">
            <Unlock size={16} /> Change Password
          </button>
          <button type="button" className="lock-menu__item lock-menu__item--danger" onClick={handleRemovePassword} role="menuitem">
            <Trash2 size={16} /> Remove Lock
          </button>
        </div>
      )}
    </div>
  )
}
